import { useEffect } from "react";
import { QuestLayout } from "../components/QuestLayout";
import { QuestObjectives } from "../components/QuestObjectives";
import {
  QuestBalance,
  QuestDisclaimer,
  QuestFlow,
  QuestLoadState,
  QuestSection,
  QuestStatus,
  RankBadge,
  SkillTags,
} from "../components/QuestPrimitives";
import {
  formatDeadline,
  isAccepting,
  questContactPath,
  questNumberLabel,
  questPath,
  questRanks,
} from "../types";
import { useQuests } from "../useQuests";

export function QuestDetail({ number }: { number: number }) {
  const data = useQuests(number);
  const quest = data.quests[0];
  useEffect(() => {
    if (quest) document.title = `${quest.title} | AIYSS Job Quest`;
  }, [quest]);
  return (
    <QuestLayout current="detail">
      <div className="content-width">
        <nav className="jq-breadcrumb" aria-label="パンくず">
          <a href="/job-quest/">Job Quest</a>
          <span aria-hidden="true">/</span>
          <a href="/job-quest/board/">Quest Board</a>
          <span aria-hidden="true">/</span>
          <span>Quest Detail</span>
        </nav>
        <QuestLoadState {...data} />
        {data.status === "ready" && quest && (
          <>
            <header className="jq-detail-hero">
              <div className="jq-detail-title">
                <div className="jq-card-meta">
                  <span className="jq-eyebrow">
                    QUEST #{questNumberLabel(quest.questNumber)}
                  </span>
                  <QuestStatus quest={quest} />
                </div>
                {quest.isDemo && (
                  <p className="jq-sample-label">
                    SAMPLE / この企業・募集条件・実績は架空です
                  </p>
                )}
                <h1>{quest.title}</h1>
                <p className="jq-detail-company">依頼主 / {quest.company}</p>
                <p>{quest.description}</p>
              </div>
              <RankBadge rank={quest.rank} />
            </header>
            <div className="jq-detail-grid">
              <div className="jq-detail-body">
                <QuestSection eyebrow="MISSION" title="あなたのMission">
                  <p className="jq-mission">{quest.mission}</p>
                  <QuestObjectives objectives={quest.objectives} />
                </QuestSection>
                {quest.challengeDescription && (
                  <QuestSection
                    eyebrow="CHALLENGE BRIEF / 応募要件の例"
                    title={quest.challengeTitle}
                  >
                    <p className="jq-mission">{quest.challengeDescription}</p>
                    {quest.references.length > 0 && (
                      <div className="jq-reference-list">
                        <p className="jq-field-label">REFERENCE / 参考論文</p>
                        {quest.references.map((reference) => (
                          <a
                            key={reference.url}
                            href={reference.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {reference.title}
                            <span>論文を開く ↗（新しいタブ）</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </QuestSection>
                )}
                <QuestSection eyebrow="REQUIRED SKILLS" title="必須スキル">
                  <SkillTags skills={quest.requiredSkills} />
                  <h3 className="jq-small-heading">
                    REQUIRED EXPERIENCE / 必須要件
                  </h3>
                  <p>{quest.requiredExperience}</p>
                  {quest.preferredExperience && (
                    <>
                      <h3 className="jq-small-heading">
                        PREFERRED EXPERIENCE / 歓迎要件
                      </h3>
                      <p className="jq-mission">{quest.preferredExperience}</p>
                    </>
                  )}
                  {quest.preferredSkills.length > 0 && (
                    <>
                      <h3 className="jq-small-heading">
                        BONUS SKILLS / 歓迎スキル
                      </h3>
                      <SkillTags skills={quest.preferredSkills} />
                    </>
                  )}
                </QuestSection>
                <QuestSection
                  eyebrow="PERSONALITY / SKILL"
                  title="企業が選考で重視すること"
                >
                  <QuestBalance quest={quest} detailed />
                </QuestSection>
                <QuestSection eyebrow="REQUIREMENTS" title="応募条件">
                  <ul className="jq-requirements">
                    {quest.applicationRequirements.map((requirement) => (
                      <li key={requirement}>{requirement}</li>
                    ))}
                  </ul>
                  <p className="jq-muted">
                    契約条件や稼働時間を確認し、学業との両立を考えてご応募ください。未成年の方は保護者と相談して進めてください。
                  </p>
                </QuestSection>
                <QuestSection eyebrow="QUEST FLOW" title="応募方法">
                  <QuestFlow steps={quest.selectionProcess} />
                  <p>
                    応募期間終了後、企業が選考します。AIYSSが応募者を推薦・選別することはありません。
                  </p>
                </QuestSection>
                {!isAccepting(quest) && (
                  <QuestSection eyebrow="CLEAR HISTORY" title="挑戦の記録">
                    {quest.pastAchievements.length > 0 ? (
                      <ul className="jq-history">
                        {quest.pastAchievements.map((entry, index) => (
                          <li key={index}>
                            <span
                              className="jq-history-check"
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                            <div>
                              <span className="jq-eyebrow">
                                {entry.year} / QUEST CLEARED
                              </span>
                              <h3>{entry.grade}がClear</h3>
                              {entry.description && <p>{entry.description}</p>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="jq-muted">
                        公開されている達成記録はまだありません。募集終了はQuestの達成を意味するものではありません。
                      </p>
                    )}
                  </QuestSection>
                )}
              </div>
              <aside className="jq-info" aria-labelledby="quest-info-title">
                <p className="jq-eyebrow">QUEST INFORMATION</p>
                <h2 id="quest-info-title">挑戦の条件</h2>
                <dl>
                  {[
                    ["Contract / 契約形態", quest.contractType],
                    ["Target / 対象", quest.target],
                    ["Location / 実施場所", quest.workLocation],
                    ["Duration / 期間", quest.duration],
                    ["Reward / 報酬", quest.reward],
                    [
                      "Positions / 募集人数",
                      quest.numberOfPositions === null
                        ? "未定"
                        : `${quest.numberOfPositions}名`,
                    ],
                    [
                      "Deadline / 応募期限",
                      `${formatDeadline(quest.deadline)}${quest.deadline ? "（日本時間）" : ""}`,
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                {quest.conditionsNote && (
                  <p className="jq-conditions-note">{quest.conditionsNote}</p>
                )}
                {isAccepting(quest) && (
                  <a
                    className="jq-button jq-info-apply"
                    href={`${questPath(quest.questNumber)}apply/`}
                  >
                    このQuestに挑戦する<span aria-hidden="true">→</span>
                  </a>
                )}
                <p className="jq-info-rank">
                  <b>Rank {quest.rank}</b> {questRanks[quest.rank]}
                  <br />
                  <small>Questの難易度を表します。</small>
                </p>
                <a
                  className="jq-text-link"
                  href={questContactPath(quest.questNumber)}
                >
                  このQuestについて質問する ↗
                </a>
              </aside>
            </div>
            <section
              className="jq-apply-cta"
              aria-labelledby="quest-apply-title"
            >
              {isAccepting(quest) ? (
                <>
                  <div>
                    <p className="jq-eyebrow">Entry</p>
                    <h2 id="quest-apply-title">エントリー</h2>
                    <p>応募後、企業による選考があります。</p>
                  </div>
                  <a
                    className="jq-button"
                    href={`${questPath(quest.questNumber)}apply/`}
                  >
                    このQuestに挑戦する <span aria-hidden="true">↗</span>
                  </a>
                </>
              ) : (
                <div>
                  <p className="jq-eyebrow">QUEST CLOSED</p>
                  <h2 id="quest-apply-title">
                    このQuestの募集は終了しました。
                  </h2>
                  <a
                    className="jq-text-link"
                    href="/job-quest/board/?status=accepting"
                  >
                    他のQuestを探す →
                  </a>
                </div>
              )}
            </section>
            <div className="jq-detail-ending">
              <a
                className="jq-button is-secondary"
                href={questContactPath(quest.questNumber)}
              >
                このQuestについて質問する →
              </a>
              <p>質問はAIYSSが受け付けます。この操作では応募されません。</p>
            </div>
            <QuestDisclaimer />
          </>
        )}
      </div>
    </QuestLayout>
  );
}
